import React, { useState, useEffect } from "react";
import {
  Grid,
  TextField,
  Box,
  Alert,
  IconButton,
  Card,
  CardContent,
  Divider,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Avatar,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import PeopleIcon from "@mui/icons-material/People";
import RefreshIcon from "@mui/icons-material/Refresh";
import DashboardLayout from "../../layouts/DashboardLayout";
import MDBox from "../../components/MDBox";
import MDButton from "../../components/MDButton";

import { usersAPI } from "../../services/api";

const AdminPage = () => {
  const [users, setUsers] = useState([]);
  const [open, setOpen] = useState(false);
  const [editUser, setEditUser] = useState(null);
  const [errorMsg, setErrorMsg] = useState("");
  const [successMsg, setSuccessMsg] = useState("");
  const [loading, setLoading] = useState(true);

  const [formData, setFormData] = useState({
    username: "",
    full_name: "",
    email: "",
    password: "",
  });

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const response = await usersAPI.listUsers();
      setUsers(response.data);
    } catch (err) {
      console.error("Error fetching users:", err);
      setErrorMsg("Error fetching users. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (user = null) => {
    setEditUser(user);
    setFormData(
      user
        ? {
            username: user.username || "",
            full_name: user.full_name || "",
            email: user.email || "",
            password: "",
          }
        : {
            username: "",
            full_name: "",
            email: "",
            password: "",
          },
    );
    setOpen(true);
  };

  const handleCloseDialog = () => {
    setOpen(false);
    setEditUser(null);
    setFormData({
      username: "",
      full_name: "",
      email: "",
      password: "",
    });
    setErrorMsg("");
  };

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleSaveUser = async () => {
    setErrorMsg("");
    if (!formData.username || !formData.full_name || !formData.email) {
      setErrorMsg("Please fill in all required fields.");
      return;
    }
    if (!validateEmail(formData.email)) {
      setErrorMsg("Please enter a valid email address.");
      return;
    }
    if (!editUser && !formData.password) {
      setErrorMsg("Password is required for new users.");
      return;
    }

    try {
      if (editUser) {
        // Update user
        const updateData = { ...formData };
        if (!updateData.password) {
          delete updateData.password; // Don't send empty password
        }
        await usersAPI.updateUser(editUser.id, updateData);
        setSuccessMsg("User updated successfully.");
      } else {
        // Add new user
        await usersAPI.createUser(formData);
        setSuccessMsg("User added successfully.");
      }
      handleCloseDialog();
      fetchUsers(); // Refresh the list
      setTimeout(() => setSuccessMsg(""), 3000);
    } catch (err) {
      console.error("Error saving user:", err);
      setErrorMsg("Error saving user. Please try again.");
    }
  };

  const handleDeleteUser = async (userId) => {
    if (!window.confirm("Are you sure you want to delete this user?")) {
      return;
    }
    try {
      await usersAPI.deleteUser(userId);
      setSuccessMsg("User deleted successfully.");
      fetchUsers(); // Refresh the list
      setTimeout(() => setSuccessMsg(""), 3000);
    } catch (err) {
      console.error("Error deleting user:", err);
      setErrorMsg("Error deleting user. Please try again.");
    }
  };

  return (
    <DashboardLayout>
      <MDBox pt={6} pb={3}>
        <MDBox mb={4}>
          <Typography variant="h3" fontWeight="bold" gutterBottom>
            User Management
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Manage user accounts, add new ones, edit or remove existing ones.
          </Typography>
        </MDBox>

        {errorMsg && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {errorMsg}
          </Alert>
        )}
        {successMsg && (
          <Alert severity="success" sx={{ mb: 3 }}>
            {successMsg}
          </Alert>
        )}

        <Box display="flex" justifyContent="flex-end" mb={4} gap={2}>
          <MDButton
            variant="outlined"
            color="info"
            startIcon={<RefreshIcon />}
            onClick={fetchUsers}
            size="large"
            disabled={loading}
          >
            Refresh
          </MDButton>
          <MDButton
            variant="gradient"
            color="info"
            startIcon={<AddIcon />}
            onClick={() => handleOpenDialog()}
            size="large"
          >
            Add User
          </MDButton>
        </Box>

        <Card elevation={4}>
          <CardContent sx={{ p: 4 }}>
            <Box display="flex" alignItems="center" mb={3}>
              <Avatar sx={{ bgcolor: "primary.main", mr: 2 }}>
                <PeopleIcon />
              </Avatar>
              <Typography variant="h5" fontWeight="bold">
                All Users
              </Typography>
            </Box>
            <Divider sx={{ mb: 3 }} />

            <TableContainer component={Paper} elevation={2}>
              <Table sx={{ minWidth: 650 }} aria-label="users table">
                <TableHead sx={{ bgcolor: "grey.100" }}>
                  <TableRow>
                    <TableCell sx={{ fontWeight: "bold", fontSize: "1rem" }}>
                      Username
                    </TableCell>
                    <TableCell sx={{ fontWeight: "bold", fontSize: "1rem" }}>
                      Full Name
                    </TableCell>
                    <TableCell sx={{ fontWeight: "bold", fontSize: "1rem" }}>
                      Email
                    </TableCell>
                    <TableCell
                      align="right"
                      sx={{ fontWeight: "bold", fontSize: "1rem" }}
                    >
                      Actions
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loading ? (
                    <TableRow>
                      <TableCell colSpan={4} align="center">
                        Loading users...
                      </TableCell>
                    </TableRow>
                  ) : (
                    users.map((user) => (
                      <TableRow
                        key={user.id}
                        sx={{ "&:hover": { bgcolor: "grey.50" } }}
                      >
                        <TableCell sx={{ fontSize: "0.95rem" }}>
                          {user.username}
                        </TableCell>
                        <TableCell sx={{ fontSize: "0.95rem" }}>
                          {user.full_name}
                        </TableCell>
                        <TableCell sx={{ fontSize: "0.95rem" }}>
                          {user.email}
                        </TableCell>
                        <TableCell align="right">
                          <IconButton
                            onClick={() => handleOpenDialog(user)}
                            color="info"
                            size="large"
                          >
                            <EditIcon />
                          </IconButton>
                          <IconButton
                            onClick={() => handleDeleteUser(user.id)}
                            color="error"
                            size="large"
                          >
                            <DeleteIcon />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>

        {/* User Dialog */}
        <Dialog open={open} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
          <DialogTitle sx={{ pb: 1 }}>
            {editUser ? "Edit User" : "Create New User"}
          </DialogTitle>
          <DialogContent sx={{ pt: 2 }}>
            <Box sx={{ pt: 1 }}>
              <TextField
                fullWidth
                label="Username"
                value={formData.username}
                onChange={(e) =>
                  setFormData({ ...formData, username: e.target.value })
                }
                sx={{ mb: 3 }}
                variant="outlined"
              />
              <TextField
                fullWidth
                label="Full Name"
                value={formData.full_name}
                onChange={(e) =>
                  setFormData({ ...formData, full_name: e.target.value })
                }
                sx={{ mb: 3 }}
                variant="outlined"
              />
              <TextField
                fullWidth
                label="Email"
                type="email"
                value={formData.email}
                onChange={(e) =>
                  setFormData({ ...formData, email: e.target.value })
                }
                sx={{ mb: 3 }}
                variant="outlined"
              />
              <TextField
                fullWidth
                label={
                  editUser
                    ? "New Password (leave empty to keep current)"
                    : "Password"
                }
                type="password"
                value={formData.password}
                onChange={(e) =>
                  setFormData({ ...formData, password: e.target.value })
                }
                sx={{ mb: 2 }}
                variant="outlined"
              />
            </Box>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 3 }}>
            <Button onClick={handleCloseDialog} size="large">
              Cancel
            </Button>
            <MDButton
              onClick={handleSaveUser}
              variant="contained"
              color="info"
              size="large"
              disabled={
                loading ||
                formData.username === "" ||
                formData.full_name === "" ||
                formData.email === "" ||
                (!editUser && formData.password === "")
              }
            >
              {editUser ? "update" : "create"}
            </MDButton>
          </DialogActions>
        </Dialog>
      </MDBox>
    </DashboardLayout>
  );
};

export default AdminPage;
